import { useState } from "react";

import { SiX } from "@icons-pack/react-simple-icons";
import { biometry, useSignal } from "@tma.js/sdk-react";

import { BlumCert, CertCard } from "entities/cert";
import { GetBlumCertificateSheet } from "features/get-blum-certificate";
import { GetCertificateSheet } from "features/get-certificate";
import { Page } from "pages/ui/layout/page";
import GalacticaLogo from "shared/assets/svg/galactica-logo.svg?react";
import UserWithStars from "shared/assets/svg/user-with-stars.svg?react";
import { useHolder } from "shared/providers/holder/context";
import { useCertificatesQuery } from "shared/tg/hooks/use-certificates-query";
import { CertificateMetadata } from "shared/tg/types";
import { Badge } from "shared/ui/badge";
import { Button } from "shared/ui/button";
import { Tab } from "shared/ui/tabs";
import { Tabs } from "shared/ui/tabs";
import { cn } from "shared/utils/cn";
import { haptic } from "shared/utils/tg";

import { NoCerts } from "./no-certs";

const tabs = ["all", "kyc", "social"] as const;

type TabType = (typeof tabs)[number];

function getMetadataByStandard(standard: CertificateMetadata["standard"]) {
  if (standard === "gip3")
    return {
      className: cn("text-white"),
      background: "bg-gradient-to-tr from-[#5D5D5D] to-[#1B1B1B]",
      // bgImage: "https://galactica.com/assets/images/home/bg-bottom-sm.webp",
      title: "X.com",
      icon: <SiX className="place-center size-12 text-black" />,
    };

  return {
    background: "from-transparent to-azure/8",
    bgImage: "https://galactica.com/assets/images/home/bg-bottom-sm.webp",
    title: "Galactica Test zkKYC",
    icon: <GalacticaLogo className="place-center size-12" />,
  };
}

export const MyCerts = () => {
  const [isBlumOpen, setIsBlumOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const holder = useHolder();

  const certificatesQuery = useCertificatesQuery({
    holderCommitment: holder.holderCommitment,
  });

  const isBiometryMounted = useSignal(biometry.isMounted);

  const filteredCerts = certificatesQuery.data?.filter((cert) => {
    if (activeTab === "all") return true;
    if (
      (cert.standard === "gip3" || cert.standard === "gip2") &&
      activeTab === "social"
    )
      return true;
    if (activeTab === "kyc" && cert.standard === "gip1") return true;
    return false;
  });

  return (
    <Page>
      <div className="mt-2 flex w-full shrink-0 overflow-x-auto px-6">
        <Tabs className="w-full">
          {tabs.map((t) => {
            return (
              <Tab
                key={t}
                isActive={activeTab === t}
                className="capitalize"
                onClick={() => {
                  setActiveTab(t);
                }}
              >
                {t === "kyc" ? "KYC" : t} Certificates
              </Tab>
            );
          })}
        </Tabs>
      </div>

      {!filteredCerts?.length && certificatesQuery.isSuccess && (
        <NoCerts className="mt-auto" />
      )}

      <div className="mt-5 mb-8 flex flex-col gap-4 px-6">
        {filteredCerts?.map((cert) => {
          if (cert.standard === "gip2")
            return (
              <BlumCert
                key={cert.certificateId}
                expirationDate={cert.timestamp}
              />
            );

          const metadata = getMetadataByStandard(cert.standard);

          return (
            <CertCard
              cardClassName={metadata.className}
              key={cert.certificateId}
              icon={metadata.icon}
              expirationDate={Date.now() + 100000}
              background={metadata.background}
              view="big"
              bgImage={metadata.bgImage}
              title={metadata.title}
            />
          );
        })}
      </div>

      <footer className="mt-auto flex flex-col px-6">
        {/* TODO: add shadow */}
        {!isBiometryMounted && (
          <Badge className="bg-grapefruit mx-auto mb-3 inline-flex">
            Biometry is not supported
          </Badge>
        )}
        <Button
          className="w-full justify-between"
          onClick={() => {
            setIsOpen(true);
          }}
        >
          Get Certificate <UserWithStars className="size-6 text-white" />
        </Button>
      </footer>
      {holder && (
        <GetCertificateSheet
          isOpen={isOpen}
          holderCommitment={holder}
          onTypeSelect={(type) => {
            if (type === "gip2") {
              setIsOpen(false);
              setIsBlumOpen(true);
            }
          }}
          onClose={() => {
            haptic("medium");
            setIsOpen(false);
          }}
        />
      )}

      {holder && (
        <GetBlumCertificateSheet
          isOpen={isBlumOpen}
          onClose={() => {
            setIsBlumOpen(false);
          }}
          onSuccess={() => {
            setIsBlumOpen(false);
          }}
          holder={holder}
        />
      )}
    </Page>
  );
};
